<template>
  <div class="register-page">
    <!-- Register Form -->
    <div>
      <!-- Header -->
      <div class="viva-page-header">
        <div class="viva-header-content">
          <div class="register-title">
            <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
            <h1 class="viva-page-title">REGISTER</h1>
          </div>
          <p class="register-subtitle text-center">
            Create your account. Use the same email and first/last name as on
            your application:
          </p>
        </div>
      </div>

      <div class="page-content">
        <form @submit.prevent="handleRegister" class="container-fluid">
          <div class="form-sections row">
            <div class="form-left col-xs-12 col-sm-6 col-md-4">
              <div class="form-group">
                <input
                  data-cy="registerForm-firstName"
                  type="text"
                  v-model="registerForm.firstName"
                  placeholder="Given / First Name"
                  @blur="validateField('firstName')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.firstName && fieldTouched.firstName,
                  }"
                />
                <span class="required-asterisk">*</span>
              </div>

              <div class="form-group">
                <input
                  data-cy="registerForm-lastName"
                  type="text"
                  v-model="registerForm.lastName"
                  placeholder="Family / Last Name"
                  @blur="validateField('lastName')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.lastName && fieldTouched.lastName,
                  }"
                />
                <span class="required-asterisk">*</span>
              </div>

              <div class="form-group">
                <input
                  data-cy="registerForm-mobileNumber"
                  type="tel"
                  v-model="registerForm.mobileNumber"
                  placeholder="Mobile Number"
                  @blur="validateField('mobileNumber')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.mobileNumber &&
                      fieldTouched.mobileNumber,
                  }"
                />
                <span class="required-asterisk">*</span>
              </div>

              <div class="form-group">
                <input
                  data-cy="registerForm-email"
                  type="email"
                  v-model="registerForm.email"
                  placeholder="Email"
                  @blur="validateField('email')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.email && fieldTouched.email,
                  }"
                />
                <span class="required-asterisk">*</span>
              </div>

              <div class="form-group">
                <input
                  data-cy="registerForm-confirmEmail"
                  type="email"
                  v-model="registerForm.confirmEmail"
                  placeholder="Confirm Email"
                  @blur="validateField('confirmEmail')"
                  class="form-input"
                  :class="{
                    'invalid-field':
                      validationErrors.confirmEmail &&
                      fieldTouched.confirmEmail,
                  }"
                />
              </div>

              <div class="form-group">
                <input
                  data-cy="registerForm-password"
                  type="password"
                  v-model="registerForm.password"
                  placeholder="Password"
                  @blur="validateField('password')"
                  class="form-input required"
                  :class="{
                    'invalid-field':
                      validationErrors.password && fieldTouched.password,
                  }"
                />
                <span class="required-asterisk">*</span>
                <div
                  v-if="validationErrors.password && fieldTouched.password"
                  class="error-message"
                >
                  {{ validationErrors.password }}
                </div>
              </div>

              <div class="form-group">
                <input
                  data-cy="registerForm-confirmPassword"
                  type="password"
                  v-model="registerForm.confirmPassword"
                  placeholder="Confirm Password"
                  @blur="validateField('confirmPassword')"
                  class="form-input"
                  :class="{
                    'invalid-field':
                      validationErrors.confirmPassword &&
                      fieldTouched.confirmPassword,
                  }"
                />
                <div
                  v-if="
                    validationErrors.confirmPassword &&
                    fieldTouched.confirmPassword
                  "
                  class="error-message"
                >
                  {{ validationErrors.confirmPassword }}
                </div>
              </div>
            </div>

            <div class="form-right col-xs-12 col-sm-6 col-md-8">
              <div class="date-section">
                <h3 class="section-title">
                  DATE OF BIRTH: <span class="required-asterisk">*</span>
                </h3>
                <div class="date-inputs">
                  <select
                    data-cy="registerForm-birthMonth"
                    v-model="registerForm.birthMonth"
                    class="form-select required"
                    @blur="validateField('birthMonth')"
                    :class="{
                      'invalid-field':
                        validationErrors.birthMonth && fieldTouched.birthMonth,
                    }"
                  >
                    <option value="" disabled>Month</option>
                    <option value="01">January</option>
                    <option value="02">February</option>
                    <option value="03">March</option>
                    <option value="04">April</option>
                    <option value="05">May</option>
                    <option value="06">June</option>
                    <option value="07">July</option>
                    <option value="08">August</option>
                    <option value="09">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                  <select
                    data-cy="registerForm-birthDay"
                    v-model="registerForm.birthDay"
                    class="form-select required"
                    @blur="validateField('birthDay')"
                    :class="{
                      'invalid-field':
                        validationErrors.birthDay && fieldTouched.birthDay,
                    }"
                  >
                    <option value="" disabled>Day</option>
                    <option
                      v-for="day in 31"
                      :key="day"
                      :value="day.toString().padStart(2, '0')"
                    >
                      {{ day }}
                    </option>
                  </select>
                  <select
                    data-cy="registerForm-birthYear"
                    v-model="registerForm.birthYear"
                    class="form-select required"
                    @blur="validateField('birthYear')"
                    :class="{
                      'invalid-field':
                        validationErrors.birthYear && fieldTouched.birthYear,
                    }"
                  >
                    <option value="" disabled>Year</option>
                    <option v-for="year in years" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="applicant-type-section">
                <h3 class="section-title">
                  WHAT ARE YOU APPLYING FOR?
                  <span class="required-asterisk">*</span>
                </h3>
                <select
                  data-cy="registerForm-applicantType"
                  v-model="applicantType"
                  class="form-select required"
                  @blur="fieldTouched.applicantType = true"
                  :class="{
                    'invalid-field':
                      !applicantType && fieldTouched.applicantType,
                  }"
                >
                  <option value="" disabled>Select one</option>
                  <option value="undergraduate">Undergraduate</option>
                  <option value="graduate">Graduate</option>
                  <option value="boarding">Day School / Boarding School</option>
                  <option value="transfer">Transfer</option>
                </select>
              </div>

              <div
                v-if="applicantType === 'undergraduate'"
                class="school-section"
              >
                <h3 class="section-title">HIGH SCHOOL:</h3>
                <p class="school-instruction">
                  Type high school name or CEEB code.
                </p>

                <div class="search-container">
                  <div class="search-input-wrapper">
                    <i class="fa fa-search search-icon"></i>
                    <input
                      data-cy="registerForm-schoolSearch"
                      type="text"
                      v-model="schoolSearchQuery"
                      @input="handleSchoolSearch"
                      placeholder="Search schools..."
                      class="school-search-input"
                      :disabled="loading || selectedSchool"
                    />
                    <i
                      v-if="schoolSearchLoading"
                      class="fa fa-spinner fa-spin viva-spinner-small search-loading-icon"
                    ></i>
                  </div>
                </div>

                <!-- Search Results Dropdown -->
                <div
                  v-if="schoolResults.length > 0"
                  class="school-results-dropdown"
                >
                  <div
                    :data-cy="'registerForm-schoolResult' + index"
                    v-for="(school, index) in schoolResults"
                    :key="school.ceeb_code || index"
                    class="school-result-item"
                    @click="selectSchool(school)"
                  >
                    <div class="school-result-content">
                      <div class="school-name">{{ school.name }}</div>
                      <div class="school-location">{{ school.location }}</div>
                      <div v-if="school.ceeb_code" class="school-ceeb">
                        CEEB: {{ school.ceeb_code }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Selected School Display -->
                <div
                  v-if="selectedSchool"
                  class="school-result-content selected-school-display"
                >
                  <div>
                    <div class="school-name">{{ selectedSchool.name }}</div>
                    <div class="school-location">
                      {{ selectedSchool.location }}
                    </div>
                    <div v-if="selectedSchool.ceeb_code" class="school-ceeb">
                      CEEB: {{ selectedSchool.ceeb_code }}
                    </div>
                  </div>
                  <button
                    v-if="selectedSchool"
                    type="button"
                    @click="clearSchoolSelection"
                    class="viva-btn viva-btn-orange clear-school-btn"
                  >
                    Change School
                  </button>
                </div>

                <!-- No Results Message -->
                <div
                  v-if="
                    schoolSearchQuery &&
                    !schoolSearchLoading &&
                    schoolResults.length === 0 &&
                    searchPerformed
                  "
                  class="no-results-message"
                >
                  No schools found for "{{ schoolSearchQuery }}". Try different
                  keywords or the 6-digit CEEB code.
                </div>

                <p class="text-link">
                  Can't find your high school? Please use 000003 (if your school
                  is in the U.S.) or 000004 (if your school is outside the U.S.)
                  and contact us at
                  <a class="link-accent" href="mailto:viva@initialview.com"
                    >viva@initialview.com</a
                  >
                  with the name of your high school and location so that we can
                  add it to our database.
                </p>
              </div>

              <div
                v-if="applicantType && applicantType !== 'undergraduate'"
                class="current-school-section"
              >
                <h3 class="section-title">
                  CURRENT OR MOST RECENT SCHOOL:
                  <span class="required-asterisk">*</span>
                </h3>
                <input
                  data-cy="registerForm-currentSchool"
                  type="text"
                  v-model="currentSchool"
                  placeholder="Please enter the full name of your current or most recent school in English"
                  class="form-input required"
                  maxlength="100"
                  @blur="fieldTouched.currentSchool = true"
                  :class="{
                    'invalid-field':
                      !currentSchool.trim() && fieldTouched.currentSchool,
                  }"
                />
              </div>

              <div class="terms-section">
                <label class="terms-checkbox">
                  <input
                    type="checkbox"
                    v-model="registerForm.agreeTerms"
                    class="checkbox-input"
                  />
                  <span
                    data-cy="registerForm-agreeTerms"
                    class="checkmark"
                  ></span>
                  <p class="text-link">
                    I agree to the
                    <a
                      class="link-accent"
                      :href="env.serverAddr + '/viva/terms-and-conditions/'"
                      target="_blank"
                      >terms and conditions</a
                    >,
                    <a
                      class="link-accent"
                      :href="env.serverAddr + '/viva/privacy-policy/'"
                      target="_blank"
                      >privacy policy</a
                    >, and
                    <a
                      class="link-accent"
                      :href="env.serverAddr + '/viva/faqs/'"
                      target="_blank"
                      >FAQs</a
                    >.
                  </p>
                </label>
              </div>

              <div
                v-if="errors.general"
                class="viva-alert viva-alert-danger"
                style="margin-bottom: 20px"
              >
                {{ errors.general }}
              </div>

              <div class="form-actions">
                <button
                  data-cy="registerForm-Submit"
                  type="submit"
                  class="create-account-btn"
                  :disabled="!isFormValid || loading"
                >
                  <span v-if="loading">
                    <i
                      class="fa fa-spinner fa-spin viva-spinner-small viva-spinner--white"
                      aria-hidden="true"
                    ></i>
                    Creating account...
                  </span>
                  <span v-else
                    >Create account <i class="fa fa-arrow-right"></i
                  ></span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div class="back-section">
        <p>
          <router-link class="back-link" to="/">
            <i class="fa fa-arrow-left"></i> Back
          </router-link>
        </p>
      </div>

      <Footer />
    </div>

    <!-- Account Exists Modal -->
    <div v-if="showAccountExistsModal" class="viva-modal-backdrop">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h3>Account Already Exists</h3>
        </div>
        <div class="viva-modal-body">
          <p>
            It looks like you already have a user account with us, perhaps
            through one of our InitialView products like Glimpse or Interview.
            Please try logging in instead.
          </p>
        </div>
        <div class="viva-modal-footer">
          <router-link class="viva-btn viva-btn-green" to="/login">
            LOGIN
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "../utils/apiClient.js";
import Footer from "@/components/Footer.vue";
import env from "@/utils/env.js";

export default {
  name: "Register",
  components: {
    Footer,
  },
  data() {
    return {
      env: env,
      showRegister: true,
      loading: false,
      errors: {},
      registerForm: {
        firstName: "",
        lastName: "",
        mobileNumber: "",
        email: "",
        confirmEmail: "",
        password: "",
        confirmPassword: "",
        birthMonth: "",
        birthDay: "",
        birthYear: "",
        agreeTerms: false,
      },
      validationErrors: {
        firstName: "",
        lastName: "",
        mobileNumber: "",
        email: "",
        confirmEmail: "",
        password: "",
        confirmPassword: "",
        birthMonth: "",
        birthDay: "",
        birthYear: "",
      },
      fieldTouched: {
        firstName: false,
        lastName: false,
        mobileNumber: false,
        email: false,
        confirmEmail: false,
        password: false,
        confirmPassword: false,
        birthMonth: false,
        birthDay: false,
        birthYear: false,
        applicantType: false,
        currentSchool: false,
      },
      applicantType: "",
      currentSchool: "",
      schoolSearchQuery: "",
      schoolSearchLoading: false,
      schoolResults: [],
      selectedSchool: null,
      searchPerformed: false,
      searchTimeout: null,
      showAccountExistsModal: false,
    };
  },
  computed: {
    years() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let year = currentYear; year >= currentYear - 100; year--) {
        years.push(year);
      }
      return years;
    },
    isFormValid() {
      return (
        this.registerForm.firstName.trim() &&
        this.registerForm.lastName.trim() &&
        this.registerForm.mobileNumber.trim() &&
        this.isValidEmail(this.registerForm.email) &&
        this.registerForm.email === this.registerForm.confirmEmail &&
        this.registerForm.password.length >= 15 &&
        this.registerForm.password === this.registerForm.confirmPassword &&
        this.registerForm.birthMonth &&
        this.registerForm.birthDay &&
        this.registerForm.birthYear &&
        this.applicantType &&
        (this.applicantType === "undergraduate"
          ? this.selectedSchool
          : this.currentSchool.trim()) &&
        this.registerForm.agreeTerms
      );
    },
  },
  methods: {
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    validateField(field) {
      this.fieldTouched[field] = true;
      switch (field) {
        case "firstName":
          this.validationErrors.firstName = this.registerForm.firstName
            ? ""
            : "First name required";
          break;
        case "lastName":
          this.validationErrors.lastName = this.registerForm.lastName
            ? ""
            : "Last name required";
          break;
        case "mobileNumber":
          this.validationErrors.mobileNumber = this.registerForm.mobileNumber
            ? ""
            : "Mobile number required";
          break;
        case "email":
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          this.validationErrors.email = emailRegex.test(this.registerForm.email)
            ? ""
            : "Invalid email";
          break;
        case "confirmEmail":
          this.validationErrors.confirmEmail =
            this.registerForm.email === this.registerForm.confirmEmail
              ? ""
              : "Emails do not match";
          break;
        case "password":
          if (!this.registerForm.password) {
            this.validationErrors.password = "Password required";
          } else if (this.registerForm.password.length < 15) {
            this.validationErrors.password =
              "Password must be 15 characters or more";
          } else {
            this.validationErrors.password = "";
          }
          break;
        case "confirmPassword":
          this.validationErrors.confirmPassword =
            this.registerForm.password === this.registerForm.confirmPassword
              ? ""
              : "Passwords do not match";
          break;
        case "birthMonth":
          this.validationErrors.birthMonth = this.registerForm.birthMonth
            ? ""
            : "Birth month required";
          break;
        case "birthDay":
          this.validationErrors.birthDay = this.registerForm.birthDay
            ? ""
            : "Birth day required";
          break;
        case "birthYear":
          this.validationErrors.birthYear = this.registerForm.birthYear
            ? ""
            : "Birth year required";
          break;
      }
    },
    handleSchoolSearch(event) {
      const query = event.target.value;
      this.schoolSearchQuery = query;

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Clear results if query is empty
      if (!query) {
        this.schoolResults = [];
        this.searchPerformed = false;
        return;
      }

      // Set loading state
      this.schoolSearchLoading = true;

      // Debounce search
      this.searchTimeout = setTimeout(() => {
        this.performSchoolSearch(query.trim());
      }, 1000);
    },

    async performSchoolSearch(query) {
      try {
        // Detect if query is 6-digit CEEB code
        const isCeebCode = /^\d{6}$/.test(query);

        let response;
        if (isCeebCode) {
          // Use CEEB validation endpoint
          response = await apiClient.post("/api/viva/validate_ceeb/", {
            ceeb: query,
          });

          if (response.data.status === 200 && response.data.hs) {
            this.schoolResults = [response.data.hs];
          } else {
            this.schoolResults = [];
          }
        } else {
          // Use school search endpoint with word-splitting support
          response = await apiClient.get("/api/viva/search_schools/", {
            params: { query },
          });

          if (response.data.status === 200) {
            this.schoolResults = response.data.hs_list || [];
          } else {
            this.schoolResults = [];
          }
        }

        this.searchPerformed = true;
      } catch (error) {
        console.error("School search error:", error);
        this.schoolResults = [];
        this.searchPerformed = true;

        // Show user-friendly error message
        if (error.response?.status === 400) {
          this.errors.general = "Please enter at least 2 characters to search.";
        } else {
          this.errors.general = "Search failed. Please try again.";
        }
      } finally {
        this.schoolSearchLoading = false;
      }
    },

    selectSchool(school) {
      this.selectedSchool = school;
      this.schoolResults = [];
      this.schoolSearchQuery = "";
      this.searchPerformed = false;
      this.errors.general = ""; // Clear any error messages
    },

    clearSchoolSelection() {
      this.selectedSchool = null;
      this.schoolSearchQuery = "";
      this.schoolResults = [];
      this.searchPerformed = false;
    },

    async handleRegister() {
      if (!this.isFormValid) return;

      this.loading = true;
      this.errors = {};

      try {
        const registrationData = {
          email: this.registerForm.email.trim(),
          password: this.registerForm.password,
          first_name: this.registerForm.firstName.trim(),
          last_name: this.registerForm.lastName.trim(),
          cell: this.registerForm.mobileNumber.trim(),
          birthday: this.registerForm.birthYear
            ? `${this.registerForm.birthYear}-${this.registerForm.birthMonth.padStart(2, "0")}-${this.registerForm.birthDay.padStart(2, "0")}`
            : null,
          applicant_type: this.applicantType,
          school_ceeb:
            this.applicantType === "undergraduate"
              ? this.selectedSchool?.ceeb_code || null
              : null,
          current_school:
            this.applicantType === "undergraduate"
              ? null
              : this.currentSchool.trim(),
        };

        const response = await apiClient.post(
          "/api/viva/register/",
          registrationData,
        );

        if (response.status === 200 && response.data.token) {
          // Store token and redirect
          sessionStorage.apitoken = response.data.token;
          apiClient.defaults.headers.common["Authorization"] =
            `Token ${response.data.token}`;

          this.$router.push("/upload");
        }
      } catch (error) {
        console.error("Registration error:", error);

        if (error.response?.status === 403) {
          this.showAccountExistsModal = true;
        } else if (error.response?.status === 400) {
          this.errors.general =
            error.response.data.message ||
            "Please check your information and try again.";
        } else {
          this.errors.general = "Registration failed. Please try again.";
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
@import "../styles/viva-classes.css";

.register-page {
  background-color: var(--viva-cream);
  min-height: 100vh;
  padding: 0;
  position: relative;
}

.viva-page-header {
  height: auto;
}

.register-title {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.register-title > * {
  margin: 0px;
}

.register-subtitle {
  color: var(--viva-dark);
  font-size: 20px;
  font-weight: 500;
  line-height: 1.4;
}

.page-content {
  display: flex;
  align-items: center;
}

.container-fluid {
  width: 100%;
}

.form-left {
  padding: 40px;
  border-right: 2px solid var(--viva-dark);
}

.form-right {
  padding: 40px;
}

/* Form styles now handled by viva-classes.css */

.date-inputs {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
}

/* .section-title and .form-select now handled by viva-classes.css */

.applicant-type-section {
  margin-bottom: 30px;
}

.current-school-section {
  margin-bottom: 30px;
}

.school-section {
  margin-bottom: 30px;
}

.school-instruction {
  color: var(--viva-orange);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 15px 0;
  line-height: 1.4;
}

.search-container {
  margin-bottom: 15px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--viva-orange);
  font-size: 18px;
}

.school-search-input {
  width: 100%;
  padding: 15px 18px 15px 50px;
  border: 2px solid var(--viva-green);
  border-radius: 8px;
  font-size: 16px;
  background: var(--viva-white);
  transition: all 0.2s ease;
}

.school-search-input:focus {
  outline: none;
  border-color: var(--viva-orange);
  box-shadow: 0 0 8px rgba(245, 176, 65, 0.2);
}

/* .school-contact now uses .text-link class from viva-classes.css */

.terms-section {
  margin-bottom: 30px;
}

.terms-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 16px;
  color: var(--viva-dark);
}

.checkbox-input {
  display: none;
}

.checkmark {
  width: 24px;
  height: 24px;
  border: 2px solid var(--viva-green);
  border-radius: 4px;
  margin-right: 15px;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.checkbox-input:checked + .checkmark {
  background-color: var(--viva-green);
  border-color: var(--viva-green);
}

.checkbox-input:checked + .checkmark::after {
  content: "✓";
  color: white;
  font-weight: bold;
  font-size: 16px;
}

.form-actions {
  text-align: right;
}

.create-account-btn {
  background: var(--viva-green);
  color: white;
  border: none;
  border-radius: 25px;
  padding: 15px 30px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  transition: all 0.2s ease;
}

.create-account-btn:hover:not(:disabled) {
  background: var(--viva-green);
  transform: translateY(-1px);
}

.create-account-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Responsive Design */
@media (max-width: 991px) {
  .date-inputs {
    flex-direction: column;
    gap: 15px;
  }
}

@media (min-width: 768px) {
  .form-sections {
    display: flex;
    align-items: center;
  }
}

@media (max-width: 767px) {
  .form-left {
    border-right: none;
  }

  .form-left,
  .form-right {
    padding: 20px;
  }

  .back-section {
    position: relative;
    bottom: auto;
    left: auto;
    padding: 20px;
    text-align: left;
  }
}

.school-results-dropdown {
  border: 1px solid var(--viva-green);
  border-top: none;
  border-radius: 0 0 8px 8px;
  background: var(--viva-white);
  max-height: 300px;
  overflow-y: auto;
  position: relative;
  width: 100%;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.school-result-item {
  padding: 12px 18px;
  cursor: pointer;
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s ease;
}

.school-result-item:hover {
  background-color: var(--viva-cream);
}

.school-result-item:last-child {
  border-bottom: none;
}

.school-result-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.school-name {
  font-weight: 600;
  color: var(--viva-dark);
  font-size: 16px;
}

.school-location {
  color: var(--viva-orange);
  font-size: 14px;
}

.school-ceeb {
  color: var(--viva-green);
  font-size: 12px;
  font-weight: 500;
}

.selected-school-display {
  margin-top: 15px;
  padding: 15px;
  background: var(--viva-white);
  border-radius: 8px;
  border: 2px solid var(--viva-green);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.clear-school-btn {
  font-size: 14px;
  width: 25%;
}

.search-loading-icon {
  position: absolute;
  right: 18px;
  margin-top: 17px;
  transform: translateY(-50%);
  color: var(--viva-orange);
}

.no-results-message {
  padding: 15px 18px;
  color: var(--viva-orange);
  font-style: italic;
  text-align: center;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f9f9f9;
  margin-top: 10px;
}

.search-container {
  position: relative;
}
</style>
